import { useContext, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { useToast } from '@givebutter/primitives/toast';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import { createBulkAction } from 'api/bulkActions';
import { useContactTypeContext } from 'assets/js/pages/contacts/ContactTypeContext';
import { ContactsViewContext } from 'assets/js/pages/contacts/ContactsViewContext';
import * as DropdownMenu from 'components/dropdown-menu';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Form from 'components/form';
import { ListItem } from 'components/list-item';
import * as Select from 'components/select';
import { TableContext } from 'components/table/TableContext';
import { BulkActionType } from 'constants/bulkActions';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface UpdateTagsDialogFormValues {
    tags: string[];
}

interface UpdateTagsDialogProps
    extends Omit<AlertDialog.AlertDialogRootProps, 'open' | 'children'> {
    action: 'add' | 'remove';
}
const UpdateTagsDialog = ({ action, onOpenChange, ...rest }: UpdateTagsDialogProps) => {
    const toast = useToast();
    const contactType = useContactTypeContext();
    const filterKey = contactType === ContactType.Individual ? 'contacts' : 'companies';
    const [isOpen, setIsOpen] = useState(false);
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: accountTags = [] } = useCurrentAccountTags();
    const newTagRef = useRef<HTMLLIElement>(null);
    const [tempTag, setTempTag] = useState('');
    const isAdd = action === 'add';
    const { table, searchTerm } = useContext(TableContext);
    const { archivedInput } = useContext(ContactsViewContext);
    const isArchived = archivedInput === 'archived';
    const tableState = table.getState();
    const { filters } = useFiltersByKey(filterKey);
    const isAllSelected = tableState.isAllRecordsSelected;
    const selectedRowIds = Object.keys(tableState.rowSelection).map(Number);
    const contactIds: number[] = isAllSelected ? [] : selectedRowIds;
    const rowCount = isAllSelected ? table.getRowCount() : contactIds.length;

    const methods = Form.useGivebutterForm<UpdateTagsDialogFormValues>({
        defaultValues: {
            tags: [],
        },
    });
    const { control, handleSubmit, watch, setError, reset } = methods;
    const tags = watch('tags');

    const options = useMemo(() => {
        return Array.from(new Set([...accountTags.map(item => item.name), ...tags]));
    }, [accountTags, tags]);

    const { mutate: submit, isPending } = useMutation({
        mutationFn: () => {
            return createBulkAction(accountId, {
                type: isAdd ? BulkActionType.ADD_CONTACT_TAGS : BulkActionType.REMOVE_CONTACT_TAGS,
                filters: isAllSelected
                    ? {
                          query: searchTerm,
                          type: contactType,
                          archiveOnly: isArchived,
                          ...filters,
                      }
                    : undefined,
                data: {
                    tags,
                    ids: isAllSelected ? undefined : contactIds,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.bulkActions(accountId) });

            toast.success(`${isAdd ? 'Add' : 'Remove'} tags action queued successfully`);
            table.toggleAllRowsSelected?.(false);
            setIsOpen(false);
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                for (let i in errors) {
                    setError('tags', {
                        message: errors[i][0],
                    });
                }
            } else {
                toast.error(
                    error.response?.data.message ??
                        (isAdd ? 'Unable to add tags' : 'Unable to remove tags'),
                );
            }
        },
    });

    return (
        <AlertDialog.Root
            open={isOpen}
            onOpenChange={isOpen => {
                setIsOpen(isOpen);
                onOpenChange?.(isOpen);
                reset();
            }}
            {...rest}
        >
            <AlertDialog.Trigger asChild onClick={() => setIsOpen(true)}>
                <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                    {capitalize(action)} tags
                </DropdownMenu.Item>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content className="max-w-lg">
                    <AlertDialog.Header>
                        <AlertDialog.Close />
                        <AlertDialog.Icon />
                    </AlertDialog.Header>
                    <AlertDialog.Body className="flex flex-col gap-4">
                        <AlertDialog.Title>{capitalize(action)} tags</AlertDialog.Title>
                        <AlertDialog.Description>
                            <AlertNotification.Root type={isAllSelected ? 'warning' : 'info'}>
                                <AlertNotification.Icon />
                                <AlertNotification.Content>
                                    <AlertNotification.Title>
                                        You are {isAdd ? 'adding' : 'removing'} tags{' '}
                                        {isAdd ? 'to' : 'from'} {maskWholeNumber(rowCount)}{' '}
                                        {pluralize(rowCount, 'contact')}
                                        {isAllSelected ? ' across multiple pages.' : '.'}
                                    </AlertNotification.Title>

                                    <AlertNotification.Description>
                                        Ensure you have properly selected the contacts you would
                                        like to edit before continuing.
                                    </AlertNotification.Description>
                                </AlertNotification.Content>
                            </AlertNotification.Root>
                        </AlertDialog.Description>
                        <Form.Provider {...methods}>
                            <form
                                onSubmit={handleSubmit(() => submit())}
                                className={'flex flex-col gap-8'}
                            >
                                <Form.Container>
                                    <Form.Label name={'tags'}>Tags</Form.Label>
                                    <Select.UseFormSearchableSelect
                                        name={'tags'}
                                        control={control}
                                        size="lg"
                                        multi={true}
                                        inputProps={{
                                            value: tempTag,
                                            placeholder: 'Tags',
                                            onChange: e => setTempTag(e.currentTarget.value),
                                            onKeyDown: e => {
                                                const value = e.currentTarget.value;
                                                if (e.key === 'Enter' && value.length > 0) {
                                                    e.preventDefault();
                                                    newTagRef.current?.click();
                                                }
                                            },
                                        }}
                                        onSelectItem={() => {
                                            setTempTag('');
                                        }}
                                    >
                                        {isAdd &&
                                            tempTag.length > 0 &&
                                            !tags.find(tag => tag === tempTag) && (
                                                <Select.SelectListItem
                                                    value={tempTag}
                                                    ref={newTagRef}
                                                >
                                                    <p className="font-light">
                                                        Add{' '}
                                                        <span className="font-medium">
                                                            {tempTag}
                                                        </span>{' '}
                                                        (press enter)
                                                    </p>
                                                </Select.SelectListItem>
                                            )}

                                        {options.map(item => (
                                            <Select.SelectListItem
                                                key={item}
                                                value={item}
                                                checkable={true}
                                            >
                                                {item}
                                            </Select.SelectListItem>
                                        ))}

                                        {!isAdd && options.length === 0 && (
                                            <ListItem className="!cursor-default text-center">
                                                No Tags were found for the selected Contacts.
                                            </ListItem>
                                        )}
                                    </Select.UseFormSearchableSelect>
                                    <Form.Error name="tags" />
                                </Form.Container>
                                <AlertDialog.Footer className="justify-between">
                                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                    <Form.SubmitButton
                                        className="flex-1"
                                        isSubmitting={isPending}
                                        disabled={tags.length === 0}
                                    >
                                        {isAdd ? 'Add' : 'Remove'} {tags.length}{' '}
                                        {pluralize(tags.length, 'tag')}
                                    </Form.SubmitButton>
                                </AlertDialog.Footer>
                            </form>
                        </Form.Provider>
                    </AlertDialog.Body>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { UpdateTagsDialog };
